import type { Params } from '@angular/router';
import {
  MASS_FILTER_FIELD_NAMES,
  MASS_FILTER_URL_VALUES_LIMIT,
  RQ_QUERY_URL_SAFE_LIMIT,
} from '@outkeep/shared';
import type { RequestsSearchBody } from '@core/types/RequestsSearch';
import { MASS_FILTER_URL_MARKER } from '@core/constants/constants';

/** Сколько значений набрано в массовых фильтрах. */
function countMassFilterValues(rqQuery: Record<string, unknown>): number {
  return MASS_FILTER_FIELD_NAMES.reduce((total, field) => {
    const value = rqQuery[field];

    if (Array.isArray(value)) {
      return total + value.length;
    }

    return typeof value === 'string' && value ? total + 1 : total;
  }, 0);
}

/**
 * Решает, помещается ли фильтр в адрес страницы.
 *
 * Основное правило — количество значений: набор, вставленный списком, в адрес
 * не влезает (nginx отдаёт 414 на URL длиннее 8 КБ, и тот же URL ломает
 * перезагрузку страницы). Проверка фактического размера — предохранитель на
 * случай аномально длинных значений и новых фильтров. На способ отправки
 * запроса это правило не влияет: список заявок всегда запрашивается POST.
 */
export function isRqQueryTooLongForUrl(
  serializedRqQuery: string | undefined,
): boolean {
  if (!serializedRqQuery) {
    return false;
  }

  if (serializedRqQuery.length > RQ_QUERY_URL_SAFE_LIMIT) {
    return true;
  }

  let rqQuery: unknown;

  try {
    rqQuery = JSON.parse(serializedRqQuery);
  } catch {
    return false;
  }

  if (!rqQuery || typeof rqQuery !== 'object' || Array.isArray(rqQuery)) {
    return false;
  }

  return (
    countMassFilterValues(rqQuery as Record<string, unknown>) >
    MASS_FILTER_URL_VALUES_LIMIT
  );
}

/**
 * Отпечаток набора фильтров для маркера в адресе страницы. Это не защита, а
 * признак соответствия: по нему видно, что сохранённое состояние — то самое,
 * из которого набор ушёл в маркер, и что ссылка не открыта с чужим или
 * устаревшим набором. Поэтому короткого несекретного хеша достаточно.
 */
export function getMassFilterFingerprint(rqQuery: unknown): string {
  let value = rqQuery;

  if (typeof rqQuery === 'string') {
    try {
      value = JSON.parse(rqQuery);
    } catch {
      value = rqQuery;
    }
  }

  // ключи сортируются: отпечаток считается от сохранённого набора и от набора
  // в адресе в разное время, и порядок ключей в объекте совпадать не обязан
  return hashString(JSON.stringify(canonicalize(value)) ?? '');
}

function canonicalize(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(canonicalize);
  }

  if (value && typeof value === 'object') {
    const source = value as Record<string, unknown>;

    return Object.keys(source)
      .sort()
      .map(key => [key, canonicalize(source[key])]);
  }

  return value;
}

/** FNV-1a: нужен устойчивый короткий отпечаток, а не стойкость к подбору. */
function hashString(source: string): string {
  let hash = 0x811c9dc5;

  for (let index = 0; index < source.length; index += 1) {
    hash ^= source.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }

  return (hash >>> 0).toString(36);
}

/** Собирает тело POST-запроса из тех же параметров, что уходят в строке запроса. */
export function toRequestsSearchBody(params: Params): RequestsSearchBody {
  const body: RequestsSearchBody = {};
  const limit = toFiniteNumber(params['limit']);
  const offset = toFiniteNumber(params['offset']);

  if (limit !== undefined) {
    body.limit = limit;
  }

  if (offset !== undefined) {
    body.offset = offset;
  }

  if (typeof params['sort'] === 'string' && params['sort']) {
    body.sort = params['sort'];
  }

  if (typeof params['rqQuery'] === 'string' && params['rqQuery']) {
    try {
      const parsed: unknown = JSON.parse(params['rqQuery']);

      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        body.rqQuery = parsed as Record<string, unknown>;
      }
    } catch {
      // битый rqQuery игнорируем: фильтр из такого адреса не восстановить,
      // а список должен открыться — пусть и без фильтра
    }
  }

  return body;
}

function toFiniteNumber(value: unknown): number | undefined {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }

  const parsed = Number(value);

  return Number.isFinite(parsed) ? parsed : undefined;
}

/**
 * Параметры для адресной строки: набор, не помещающийся в URL, туда не
 * попадает — браузер отправит этот адрес на перезагрузке, и nginx ответит
 * 414 уже при отдаче index.html. Вместо набора остаётся маркер.
 */
export function toListUrlQueryParams(params: Params): Params {
  if (!isRqQueryTooLongForUrl(params['rqQuery'] as string | undefined)) {
    return params;
  }

  const urlParams: Params = {
    ...params,
    [MASS_FILTER_URL_MARKER]: getMassFilterFingerprint(params['rqQuery']),
  };

  delete urlParams['rqQuery'];

  return urlParams;
}

/**
 * Фильтр списка заявок по параметрам адреса: массовый набор значений в URL
 * не хранится, поэтому по маркеру он берётся из сохранённого состояния
 * списка. Одна точка истины для списка, полей фильтров и чипов.
 *
 * Маркер применяется только к тому набору, от которого он посчитан: ссылку с
 * маркером можно открыть на другом устройстве или после смены фильтра, и
 * подставлять туда случайный сохранённый набор нельзя. При несовпадении
 * список открывается без массового фильтра.
 */
export function resolveListRqQuery(
  params: Params,
  savedRqQuery: unknown,
): Record<string, unknown> {
  const marker = params[MASS_FILTER_URL_MARKER];

  if (marker) {
    if (
      !savedRqQuery ||
      typeof savedRqQuery !== 'object' ||
      Array.isArray(savedRqQuery)
    ) {
      return {};
    }

    return getMassFilterFingerprint(savedRqQuery) === marker
      ? { ...(savedRqQuery as Record<string, unknown>) }
      : {};
  }

  if (typeof params['rqQuery'] !== 'string' || !params['rqQuery']) {
    return {};
  }

  try {
    const parsed: unknown = JSON.parse(params['rqQuery']);

    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? { ...(parsed as Record<string, unknown>) }
      : {};
  } catch {
    return {};
  }
}
